import React, { useState, useRef } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import { useCampaigns } from '../hooks/useCampaigns';

interface ImageUploadProps {
    campaignId: string;
    onUploadComplete: (imageUrl: string) => void;
}

const ImageUpload: React.FC<ImageUploadProps> = ({ campaignId, onUploadComplete }) => {
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { uploadImage } = useCampaigns();

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Reset states
        setError(null);
        setUploading(true);

        try {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                throw new Error('Please select an image file');
            }

            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                throw new Error('Image size should be less than 5MB');
            }

            // Upload image
            const imageUrl = await uploadImage(file, campaignId);
            if (!imageUrl) {
                throw new Error('Failed to upload image');
            }
            onUploadComplete(imageUrl);
            
        } catch (err) {
            console.error('Image upload error:', err);
            setError(err instanceof Error ? err.message : 'Failed to upload image');
        } finally {
            setUploading(false);
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    return (
        <Box sx={{ textAlign: 'center', p: 2 }}>
            <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                ref={fileInputRef}
                onChange={handleFileSelect}
            />
            <Button
                variant="outlined"
                startIcon={uploading ? <CircularProgress size={20} /> : <CloudUpload />}
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                sx={{ mb: 1 }}
            >
                {uploading ? 'Uploading...' : 'Upload Image'}
            </Button>
            {error && (
                <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                    {error}
                </Typography>
            )}
        </Box>
    );
};

export default ImageUpload; 